import SmartField from './SmartField'
import NumberFormatter from '../number_formatter'
/**
 * Smart field for editing decimal numbers.
 *
 * @param fieldElement HTML input element (can be wrapped into jQuery)
 * @param allowEmpty boolean - whether an empty string is treated as a valid value
 * @param maxLength - maximum number of characters that can be entered. If not specified, then there's no limit.
 * @param maxFractionDigits - maximum number of digits in fractional part.
 *        If entered value contains more fractional digits, it will be rounded during re-formatting.
 * @param [decimalSeparator] - symbol to use as decimal separator when formatting and parsing. By default '.' (dot).
 *                             NB! When parsing, '.' and ',' are always allowed as decimal separators, unless one of
 *                             them is set as digit grouping symbol.
 * @param [allowNegative] - whether to allow entering negative values. True by default
 * @param [digitGroupingSymbol] - digit grouping symbol to use when formatting and parsing. Default is empty string.
 * @param [invalidHint] - text to display as tooltip when the field is in 'invalid' state
 * @param [allowZero] - whether to allow entering zero value. True by default
 * @param [maxValue] - maximum value for this field (value should be < than maxValue)
 *
 * TODO: allow/disallow fractions? limit max/min?
 */
function DecimalField(fieldElement, allowEmpty, maxLength, maxFractionDigits, decimalSeparator, allowNegative, digitGroupingSymbol,
                      invalidHint, allowZero, maxValue)
{
    /**
     * @return string the current value
     */
    this.getValue = function()
    {
        return smartField.getValue();
    };

    /**
     * @return Number the current value as number
     */
    this.getValueAsNumber = function()
    {
        return convertToNumber( this.getValue() );
    };

    /**
     * Sets the new value to the field.
     * This method treats the given parameter as a string, as if the user entered the value from keyboard.
     * Note that this will trigger the 'change' event handler which will trim, validate and re-format the given value.
     * @param value string new value to set
     */
    this.setValue = function (value)
    {
        smartField.setValue(value);
    };

    /**
     * Sets the new value to the field.
     * This method treats the given parameter as a javascript number, and formats it as string using the rules specified
     * in the constructor.
     * Note that the validation will also occur, because the given number may be negative while negatives may be disallowed.
     * @param value number
     */
    this.setValueAsNumber = function (value)
    {
        this.setValue( formatNumber(value) );
    };

    /**
     * Trims, validates and re-formats the current value.
     * This function is invoked automatically on 'change' event, but sometimes it is useful to invoke it manually.
     */
    this.process = function ()
    {
        smartField.process();
    };

    /**
     * @return boolean whether the current value is valid
     */
    this.isValid = function()
    {
        return smartField.isValid();
    };

    /**
     * @return boolean whether the current value is an empty string
     */
    this.isEmpty = function()
    {
        return smartField.isEmpty();
    };

    /**
     * Removes the 'invalid' field status, if set, even if the text is still invalid.
     * This effectively removes the CSS 'invalid' class from the field.
     */
    this.clearValidationResult = function()
    {
        smartField.clearValidationResult();
    };

    this.focus = function()
    {
        smartField.focus();
    };

    /**
     * Sets decimal separator and then re-formats value if the field is valid
     * @param newDecimalSeparator
     */
    this.setDecimalSeparator = function( newDecimalSeparator )
    {
        var wasValid = this.isValid();
        var value;
        if ( wasValid )
            value = this.getValueAsNumber();

        decimalSeparator = newDecimalSeparator;
        numberFormatter = new NumberFormatter(decimalSeparator, digitGroupingSymbol, maxFractionDigits, true);

        if ( wasValid )
            this.setValueAsNumber( value );
    };

    /**
     * Sets digit grouping symbol and then re-formats value if the field is valid
     * @param newDigitGroupingSymbol
     */
    this.setDigitGroupingSymbol = function( newDigitGroupingSymbol )
    {
        var wasValid = this.isValid();
        var value;
        if ( wasValid )
            value = this.getValueAsNumber();

        digitGroupingSymbol = newDigitGroupingSymbol;
        numberFormatter = new NumberFormatter(decimalSeparator, digitGroupingSymbol, maxFractionDigits, true);

        if ( wasValid )
            this.setValueAsNumber( value );
    };

    /**
     * Sets new invalid hint
     * @param newInvalidHint new hint to set
     */
    this.setInvalidHint = function( newInvalidHint )
    {
        smartField.setInvalidHint( newInvalidHint );
    };

    // ---------- private ----------

    function charFilter(charCode)
    {
        var sym = String.fromCharCode( charCode );

        return sym >= '0' && sym <= '9'
               || sym == '-' && allowNegative
               || sym == '.'
               || sym == ','
               || sym == decimalSeparator
               || sym == digitGroupingSymbol;
    }

    function convertToNumber(text)
    {
        text = numberFormatter.normalize(text)

        return Number( text );
    }

    function formatNumber(number)
    {
        return numberFormatter.format(number);
    }

    function getValidator()
    {
        return function (text) {
            var number = convertToNumber(text);
            return !isNaN(number) && (number >= 0 || allowNegative) && (number != 0 || allowZero) && (!maxValue || number < maxValue);
        };
    }

    function getFormatter()
    {
        return function (text) {
            return formatNumber( convertToNumber(text) );
        }
    }

    if (allowNegative == undefined)
        allowNegative = true;

    if (allowZero == undefined)
        allowZero = true;

    if (decimalSeparator == undefined)
        decimalSeparator = '.';

    if (digitGroupingSymbol == undefined)
        digitGroupingSymbol = '';

    var numberFormatter = new NumberFormatter(decimalSeparator, digitGroupingSymbol, maxFractionDigits, true);

    var smartField = new SmartField(fieldElement,
        charFilter,
        getValidator(),
        getFormatter(), allowEmpty, maxLength, invalidHint, maxValue);
}

export default DecimalField



// WEBPACK FOOTER //
// ./web/js/controls/DecimalField.js